import { ReactNode, useEffect, useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { AppTab } from "./AppNavbar";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { Button } from "reactstrap";

interface Props {
  columns: AppTab[];
  onDragEnd?: (newOrder: string[]) => void;
  onCancel?: () => void;
  showDisabled?: boolean;
}

export default function ReorderColumns({
  columns,
  onDragEnd,
  onCancel,
  showDisabled = true,
}: Props) {
  const [colIds, setColIds] = useState([] as string[]);

  useEffect(() => {
    if (!Array.isArray(columns)) return;

    setColIds(columns.map((col) => col.activeValue));
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToHorizontalAxis]}
      >
        <div className="d-flex justify-content-between">
          <SortableContext
            items={colIds}
            strategy={horizontalListSortingStrategy}
          >
            <div
              className="d-flex rs-nav rs-nav-subtle rs-nav-horizontal scrollbar-below"
              style={{
                height: 55,
                overflowX: "auto",
                overflowY: "hidden",
              }}
            >
              {colIds.map((colID) => {
                const col = columns.find((col) => col.activeValue === colID);

                if (!col || !col?.activeValue) return null;

                if (col?.disabled && !showDisabled) return null;

                return (
                  <SortableColumn
                    key={col.activeValue}
                    id={col.activeValue}
                    title={col?.title}
                    disabled={col?.disabled}
                  />
                );
              })}
            </div>
          </SortableContext>
          <div style={{ minWidth: 130 }} className="ml-2">
            <Button size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={handleSave}
              className="m-0"
            >
              Save
            </Button>
          </div>
        </div>
      </DndContext>
    </>
  );

  function handleDragEnd(event: any) {
    const { active, over } = event;

    if (active.id === over.id) return;

    const oldIndex = colIds.indexOf(active.id);
    const newIndex = colIds.indexOf(over.id);

    const newColIds = arrayMove(colIds, oldIndex, newIndex);

    setColIds(newColIds);
  }

  function handleSave() {
    onDragEnd && onDragEnd(colIds);
  }
}

export function SortableColumn(props: {
  title: ReactNode;
  id: string;
  disabled?: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
  };

  const Item = (
    <div className="d-inline-block rs-nav-item no-toggle border border-superlight bg-ultralight bg-dropdown-hover--hover">
      <div style={{ height: "23px" }}>
        <div className="d-inline-block pr-2 ml--1">
          <i
            className="mdi mdi-drag"
            style={{
              fontSize: 22,
              lineHeight: 1,
              position: "relative",
              top: 1,
            }}
          />
        </div>
        <div
          className="d-inline-block text-sm"
          style={{ position: "relative", top: -2 }}
        >
          {props.title}
        </div>
      </div>
    </div>
  );

  if (props.disabled) return Item;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {Item}
    </div>
  );
}
